import type React from "react";
import { startTransition, useEffect, useMemo, useState } from "react";
import WizardsConfig from "app/pages/digital-suite/home-page/components/ActivationWizard/WizardsConfig";
import {
    getStepsState,
    setStepsState,
    resetSteps,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardApi";
import type {
    WizardRole,
    IStepState,
    IStepMetadata,
    IActivationWizard,
    ICompletionStep,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";

interface IUseActivationWizardSteps {
    steps: Array<React.FC<IStepMetadata>>;
    stepsState: Array<IStepState>;
    setStepState: (state: IStepState, serverOnly?: boolean) => void;
    getCompletionStep: (allStepsSkipped: boolean) => ICompletionStep;
    resetSteps: () => void;
}

export const useActivationWizardSteps = (role: WizardRole): IUseActivationWizardSteps => {
    const [cachedState, setCachedState] = useState<Array<IStepState>>(null);
    const [updateServerFlag, setUpdateServerFlag] = useState<boolean>(false);
    const wizard = useMemo<IActivationWizard>(() => WizardsConfig[role], [role]);

    useEffect(() => {
        getStepsState(role).then((res) => setCachedState(res));
    }, [role]);

    useEffect(() => {
        if (!updateServerFlag || !cachedState) {
            return;
        }

        setStepsState(role, cachedState);
        setUpdateServerFlag(false);
    }, [updateServerFlag]);

    const setStepState = (stepState: IStepState, serverOnly = false): void => {
        if (serverOnly) {
            setStepsState(role, [...cachedState.filter((step) => step.index !== stepState.index), stepState]);
        } else {
            startTransition(() => {
                setUpdateServerFlag(true);
                setCachedState((prev) => [...prev.filter((step) => step.index !== stepState.index), stepState]);
            });
        }
    };

    return {
        steps: wizard.steps,
        stepsState: cachedState,
        setStepState: setStepState,
        getCompletionStep: wizard.getCompletionStep,
        resetSteps: () => resetSteps(role, cachedState),
    };
};
